import { useCallback, useEffect, useRef, useState } from 'react';
import { createTruthRefreshCooldown } from './truthRefreshCooldown';

/** App tab id of Control Centre (the hub). */
export const DEFAULT_HUB_TAB_ID = 'fleet-data-hub';

/**
 * Bumps a refresh key when the Control Centre hub tab becomes active again
 * or the browser tab regains focus.
 *
 * - **Hub return** (leave Control Centre → come back): always refreshes.
 *   A prior mount/period fetch must not block this — that was why Driver
 *   looked frozen until Refresh All.
 * - **document.visibilitychange** (alt-tab): gated by `minIntervalMs` so
 *   focus thrash does not hammer Trino. `markTruthFetch()` stamps that gap.
 *
 * Enabling alone (e.g. switching to Driver while already on the hub) does not
 * bump; callers should force-bump on section enter if they need that.
 */
export function useVisibilityTruthRefresh(options: {
  enabled: boolean;
  activeTab: string;
  minIntervalMs: number;
  hubTabId?: string;
}): {
  refreshKey: number;
  bump: (opts?: { force?: boolean }) => void;
  markTruthFetch: () => void;
} {
  const { enabled, activeTab, minIntervalMs, hubTabId = DEFAULT_HUB_TAB_ID } = options;
  const [refreshKey, setRefreshKey] = useState(0);
  const cooldownRef = useRef(createTruthRefreshCooldown(minIntervalMs));
  const minIntervalRef = useRef(minIntervalMs);
  const wasHubActiveRef = useRef(activeTab === hubTabId);

  if (minIntervalRef.current !== minIntervalMs) {
    minIntervalRef.current = minIntervalMs;
    // Preserve the stamp so tightening/loosening the interval does not
    // immediately re-arm a visibility bump.
    cooldownRef.current = createTruthRefreshCooldown(
      minIntervalMs,
      cooldownRef.current.lastAt(),
    );
  }

  const bump = useCallback((opts?: { force?: boolean }) => {
    if (!enabled && !opts?.force) return;
    if (!cooldownRef.current.tryBump(Date.now(), opts?.force)) return;
    setRefreshKey((k) => k + 1);
  }, [enabled]);

  const markTruthFetch = useCallback(() => {
    cooldownRef.current.mark(Date.now());
  }, []);

  useEffect(() => {
    const hubActive = activeTab === hubTabId;
    const wasActive = wasHubActiveRef.current;
    wasHubActiveRef.current = hubActive;
    if (!enabled) return;
    if (!hubActive || wasActive) return;
    // Always re-read when coming back to Control Centre — cooldown is for
    // visibilitychange spam only.
    bump({ force: true });
  }, [activeTab, enabled, hubTabId, bump]);

  useEffect(() => {
    if (!enabled) return;
    if (activeTab !== hubTabId) return;

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        bump();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [enabled, activeTab, hubTabId, bump]);

  return { refreshKey, bump, markTruthFetch };
}
